import axios from 'axios';
import moment, { Moment } from 'moment';
import React from 'react';

import Banner from 'plaid-threads/Banner';
import Button from 'plaid-threads/Button';
import DateInput from 'plaid-threads/DateInput';
import Dropdown from 'plaid-threads/Dropdown';
import DropdownLink from 'plaid-threads/DropdownLink';
import IconButton from 'plaid-threads/IconButton';
import { AddCircle, Remove } from 'plaid-threads/Icons';
import NumberInput from 'plaid-threads/NumberInput';
import TextInput from 'plaid-threads/TextInput';

import Whitespace from '../components/Whitespace';
import { AppContext } from '../App';
import { CountryCode, Currency } from '../constants';
import { InternalOptions } from '../types';

enum ConsentType {
  SWEEPING = 'SWEEPING',
  COMMERCIAL = 'COMMERCIAL',
}

enum PeriodicAlignment {
  CALENDAR = 'calendar',
  CONSENT = 'consent',
}

enum PeriodicInterval {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

interface PeriodicAmount {
  amount: string;
  interval: PeriodicInterval;
  alignment: PeriodicAlignment;
}

interface Constraints {
  validFrom: Moment | null;
  validTo: Moment | null;
  maxPaymentAmount: string;
  reference: string;
  periodicAmounts: PeriodicAmount[];
}

interface CreateConsentProps {
  onCreated: (consentId: string) => void;
}

const CreateConsent: React.FC<CreateConsentProps> = ({ onCreated }) => {
  const [consentType, setConsentType] = React.useState<ConsentType>(
    ConsentType.SWEEPING,
  );
  const [constraints, setConstraints] = React.useState<Constraints>({
    validFrom: moment(),
    validTo: moment().add(1, 'month'),
    maxPaymentAmount: '5',
    reference: 'demo123',
    periodicAmounts: [
      {
        amount: '20',
        interval: PeriodicInterval.MONTH,
        alignment: PeriodicAlignment.CALENDAR,
      },
    ],
  });
  const [consentError, setConsentError] = React.useState<string>('');
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const {
    countryCode,
    currency,
    forceDesktop,
    recipientId,
    customizationName,
  } = React.useContext(AppContext);

  function onPeriodicAmountChange(
    index: number,
    periodicAmount: PeriodicAmount,
  ) {
    const periodicAmounts = [...constraints.periodicAmounts];
    periodicAmounts[index] = periodicAmount;
    setConstraints({ ...constraints, periodicAmounts });
  }

  function onPeriodicAmountRemove(
    index: number,
    periodicAmount: PeriodicAmount,
  ) {
    const periodicAmounts = constraints.periodicAmounts.filter(
      (periodicAmount, i) => {
        return i !== index;
      },
    );
    setConstraints({ ...constraints, periodicAmounts });
  }

  function onContinueClick() {
    if (submitting) {
      return;
    }

    setConsentError('');
    setSubmitting(true);

    createConsent(
      constraints,
      countryCode,
      currency,
      recipientId,
      customizationName,
      consentType,
    )
      .then(({ consentId, linkToken }) => {
        emulateLink(consentId, linkToken, forceDesktop, (consentId) => {
          onCreated(consentId);
        });
      })
      .catch((error) => {
        setConsentError(error?.response?.data?.display_message || error + '');
      })
      .then(() => {
        setSubmitting(false);
      });
  }

  function onPeriodicLimitAddClick() {
    const periodicAmounts = [...constraints.periodicAmounts];
    periodicAmounts.push({
      amount: '',
      interval: PeriodicInterval.WEEK,
      alignment: PeriodicAlignment.CONSENT,
    });
    setConstraints({ ...constraints, periodicAmounts });
  }

  return (
    <div className="Consents__Create">
      {consentError && (
        <Banner error title="Error">
          {consentError}
        </Banner>
      )}
      <div className="Consents__Create__ConsentTypeToggle">
        <span
          className="Consents__Create__ConsentTypeToggle__Link"
          onClick={() =>
            setConsentType(
              consentType === ConsentType.SWEEPING
                ? ConsentType.COMMERCIAL
                : ConsentType.SWEEPING,
            )
          }
        >
          {consentType === ConsentType.SWEEPING
            ? 'Switch to Commercial VRP'
            : 'Switch to Sweeping VRP'}
        </span>
        {consentType === ConsentType.COMMERCIAL && (
          <span className="Consents__Create__ConsentTypeToggle__Badge">
            Commercial
          </span>
        )}
      </div>
      <div className="Consents__row">
        <DateInput
          value={constraints.validFrom}
          label="Valid from:"
          id="validFrom"
          defaultValue={null}
          disabledDate={() => false}
          onChange={(validFrom) =>
            setConstraints({ ...constraints, validFrom })
          }
          className="Consents__Create__DateInput"
        />
        <Whitespace width="1rem" />
        <DateInput
          value={constraints.validTo}
          label="Valid until:"
          id="validTo"
          defaultValue={null}
          disabledDate={() => false}
          onChange={(validTo) => setConstraints({ ...constraints, validTo })}
          className="Consents__Create__DateInput"
        />
      </div>
      <div className="Consents__row">
        <NumberInput
          value={constraints.maxPaymentAmount + ''}
          id="maxPaymentAmount"
          label="Max amount per single payment"
          onChange={(e) =>
            setConstraints({
              ...constraints,
              maxPaymentAmount: e.currentTarget.value,
            })
          }
        />
        <Whitespace width="1rem" />
        <TextInput
          value={constraints.reference}
          id="reference"
          label="Reference"
          onChange={(e) =>
            setConstraints({ ...constraints, reference: e.currentTarget.value })
          }
        />
      </div>
      {constraints.periodicAmounts.map((periodicAmount, index) => {
        return (
          <PeriodicAmountRow
            key={index}
            periodicAmount={periodicAmount}
            onChange={onPeriodicAmountChange.bind(null, index)}
            onRemove={onPeriodicAmountRemove.bind(null, index)}
          />
        );
      })}
      <div className="Consents__row">
        <Button
          secondary
          inline
          centered
          size={32}
          onClick={onPeriodicLimitAddClick}
          className="Consents__Create__AddPeriodicLimit"
        >
          Add new periodic limit
          <AddCircle />
        </Button>
      </div>
      <div className="Consents__continueButton">
        <Button
          size={32}
          inline
          animatedCaret
          onClick={onContinueClick}
          disabled={
            !constraints.maxPaymentAmount ||
            !constraints.reference ||
            constraints.periodicAmounts.length === 0
          }
          submitting={submitting}
        >
          Launch Demo
        </Button>
      </div>
    </div>
  );
};

const ALIGNMENT_OPTIONS: Array<{ label: string; value: PeriodicAlignment }> = [
  { label: 'calendar', value: PeriodicAlignment.CALENDAR },
  { label: 'consent', value: PeriodicAlignment.CONSENT },
];
const INTERVAL_OPTIONS: Array<{ label: string; value: PeriodicInterval }> = [
  { label: 'day', value: PeriodicInterval.DAY },
  { label: 'week', value: PeriodicInterval.WEEK },
  { label: 'month', value: PeriodicInterval.MONTH },
  { label: 'year', value: PeriodicInterval.YEAR },
];

interface PeriodicAmountRowProps {
  periodicAmount: PeriodicAmount;
  onChange: (periodicAmount: PeriodicAmount) => void;
  onRemove: (periodicAmount: PeriodicAmount) => void;
}

const PeriodicAmountRow: React.FC<PeriodicAmountRowProps> = ({
  periodicAmount,
  onChange,
  onRemove,
}) => {
  const [alignmentSelectorOpen, setAlignmentSelectorOpen] =
    React.useState<boolean>(false);
  const [intervalSelectorOpen, setIntervalSelectorOpen] =
    React.useState<boolean>(false);

  function onAmountChange(amount: string) {
    onChange({ ...periodicAmount, amount });
  }

  function onAlignmentChange(alignment: PeriodicAlignment) {
    onChange({ ...periodicAmount, alignment });
  }

  function onIntervalChange(interval: PeriodicInterval) {
    onChange({ ...periodicAmount, interval });
  }

  function onRemoveClick() {
    onRemove(periodicAmount);
  }

  return (
    <div className="Consents__row">
      <span>Max</span>
      <Whitespace width="1rem" />
      <NumberInput
        value={periodicAmount.amount + ''}
        id={'maxPeriodicAmount'}
        label=""
        onChange={(e) => onAmountChange(e.currentTarget.value)}
        className="Consents__Create__PeriodicAmount"
      />
      <Whitespace width="1rem" />
      <span>GBP per</span>
      <Whitespace width="1rem" />
      <Dropdown
        onChange={(option) => {
          setAlignmentSelectorOpen(false);
          onAlignmentChange(option.value as PeriodicAlignment);
        }}
        options={ALIGNMENT_OPTIONS}
        target={
          <DropdownLink
            expanded={alignmentSelectorOpen}
            onClick={() => setAlignmentSelectorOpen(!alignmentSelectorOpen)}
          >
            {periodicAmount.alignment}
          </DropdownLink>
        }
        isOpen={alignmentSelectorOpen}
        value={ALIGNMENT_OPTIONS.find(
          (option) => option.value === periodicAmount.alignment,
        )}
      />
      <Whitespace width="1rem" />
      <Dropdown
        onChange={(option) => {
          setIntervalSelectorOpen(false);
          onIntervalChange(option.value as PeriodicInterval);
        }}
        options={INTERVAL_OPTIONS}
        target={
          <DropdownLink
            expanded={intervalSelectorOpen}
            onClick={() => setIntervalSelectorOpen(!intervalSelectorOpen)}
          >
            {periodicAmount.interval}
          </DropdownLink>
        }
        isOpen={intervalSelectorOpen}
        value={INTERVAL_OPTIONS.find(
          (option) => option.value === periodicAmount.interval,
        )}
      />
      <div className="Consents__Create__PeriodicLimitActions">
        <IconButton
          accessibilityLabel="Remove"
          icon={<Remove />}
          onClick={onRemoveClick}
        />
      </div>
    </div>
  );
};

function createConsent(
  constraints: Constraints,
  countryCode: Array<CountryCode>,
  currency: Currency,
  recipientId: string | null,
  customizationName: string | null,
  consentType: ConsentType,
): Promise<{ consentId: string; linkToken: string }> {
  return axios
    .post(
      `${process.env.REACT_APP_SERVER_URL}/token/consent`,
      {
        countryCode,
        currency,
        constraints,
        recipientId,
        customizationName,
        consentType,
      },
      { withCredentials: true },
    )
    .then((response) => {
      const { consent_id, link_token } = response.data;

      if (!consent_id || !link_token) {
        return Promise.reject('Failed to create Payment Consent');
      }

      return { consentId: consent_id, linkToken: link_token };
    });
}

function emulateLink(
  consentId: string,
  linkToken: string,
  forceDesktop: boolean,
  onCreated: (consentId: string) => void,
) {
  try {
    const internalOptions: InternalOptions = {};
    if (forceDesktop) {
      internalOptions.forceDesktop = true;
    }
    // @ts-ignore
    const plaidLink = Plaid.create(
      {
        token: linkToken,
        onSuccess() {
          onCreated(consentId);
        },
      },
      internalOptions,
    );
    plaidLink.open();
    // Change Link's zIndex so the demo banner can be seen
    const iframes = Array.from(document.getElementsByTagName('iframe'));
    iframes.forEach((iframe) => (iframe.style.zIndex = '2147483646'));
  } catch (error) {
    console.error('Smth went wrong::', error);
  }
}

export default CreateConsent;
